import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios, { API } from '../services/api';
import { toast } from 'sonner';
import {
  Users, Pulse, ListChecks, FileText,
  MagnifyingGlass, CaretRight, ArrowClockwise,
  Warning, CheckCircle, XCircle, Clock,
  Eye, UserCircleMinus, Flag, ArrowLeft
} from '@phosphor-icons/react';

const _API = API; // Use the shared API base from services/api.js

/* ── Shared Components ── */
const Badge = ({ children, variant = 'default' }) => {
  const colors = {
    default: 'bg-slate-100 text-slate-600',
    success: 'bg-emerald-50 text-emerald-700',
    warning: 'bg-amber-50 text-amber-700',
    error: 'bg-red-50 text-red-700',
    info: 'bg-blue-50 text-blue-700',
  };
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${colors[variant]}`}>{children}</span>;
};

const StatCard = ({ label, value, sub }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <p className="text-[11px] text-slate-400 uppercase tracking-wider mb-1">{label}</p>
    <p className="text-2xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>{value}</p>
    {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
  </div>
);

const EmptyState = ({ message }) => (
  <div className="text-center py-12 text-slate-300 text-sm">{message}</div>
);

const formatDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return d; }
};


/* ══════════════════════════════════════════════════
   MODULE 1: USER MANAGEMENT
   ══════════════════════════════════════════════════ */
const UsersModule = () => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.set('search', search);
      const { data } = await axios.get(`${_API}/studio/users?${params}`);
      setUsers(data.users);
      setTotal(data.total);
    } catch { toast.error('Failed to load users'); }
    setLoading(false);
  }, [search, page]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const loadDetail = async (userId) => {
    setSelected(userId);
    setDetail(null);
    try {
      const { data } = await axios.get(`${_API}/studio/users/${userId}`);
      setDetail(data);
    } catch { toast.error('Failed to load user'); }
  };

  const handleFlag = async (userId, flagged) => {
    const reason = flagged ? prompt('Reason for flagging:') : '';
    if (flagged && !reason) return;
    await axios.patch(`${_API}/studio/users/${userId}/flag`, { flagged, reason });
    toast.success(flagged ? 'User flagged' : 'Flag removed');
    loadDetail(userId);
    fetchUsers();
  };

  const handleDeactivate = async (userId) => {
    if (!confirm('Deactivate this user? They will not be able to log in.')) return;
    await axios.patch(`${_API}/studio/users/${userId}/deactivate`);
    toast.success('User deactivated');
    loadDetail(userId);
    fetchUsers();
  };

  const handleActivate = async (userId) => {
    await axios.patch(`${_API}/studio/users/${userId}/activate`);
    toast.success('User activated');
    loadDetail(userId);
    fetchUsers();
  };

  if (selected && detail) {
    return (
      <div>
        <button onClick={() => { setSelected(null); setDetail(null); }} className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-600 mb-4 transition-colors" data-testid="back-to-users">
          <ArrowLeft size={14} /> Back to users
        </button>
        <div className="grid lg:grid-cols-3 gap-4 mb-6">
          <StatCard label="Conditions" value={detail.health_summary?.conditions || 0} />
          <StatCard label="Medications" value={detail.health_summary?.medications || 0} />
          <StatCard label="Chat Messages" value={detail.health_summary?.chat_messages || 0} />
          <StatCard label="Appointments" value={detail.health_summary?.appointments || 0} />
          <StatCard label="Documents" value={detail.health_summary?.documents || 0} />
          <StatCard label="Courses" value={detail.health_summary?.courses || 0} />
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-5 mb-4">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-base font-semibold text-[#0F172A]">{detail.name || 'No name'}</h3>
              <p className="text-sm text-slate-400">{detail.email}</p>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant={detail.role === 'admin' ? 'info' : 'default'}>{detail.role}</Badge>
                {detail.active === false && <Badge variant="error">Deactivated</Badge>}
                {detail.flagged && <Badge variant="warning">Flagged: {detail.flag_reason}</Badge>}
                {detail.onboarding_completed && <Badge variant="success">Onboarded</Badge>}
              </div>
            </div>
            <div className="flex gap-2">
              {detail.flagged ? (
                <button onClick={() => handleFlag(detail.id, false)} className="text-xs bg-slate-100 text-slate-600 px-3 py-1.5 rounded-lg hover:bg-slate-200 transition-colors">Remove flag</button>
              ) : (
                <button onClick={() => handleFlag(detail.id, true)} className="text-xs bg-amber-50 text-amber-700 px-3 py-1.5 rounded-lg hover:bg-amber-100 transition-colors" data-testid="flag-user-btn"><Flag size={12} weight="bold" className="inline mr-1" />Flag</button>
              )}
              {detail.active !== false ? (
                <button onClick={() => handleDeactivate(detail.id)} className="text-xs bg-red-50 text-red-600 px-3 py-1.5 rounded-lg hover:bg-red-100 transition-colors" data-testid="deactivate-user-btn"><UserCircleMinus size={12} weight="bold" className="inline mr-1" />Deactivate</button>
              ) : (
                <button onClick={() => handleActivate(detail.id)} className="text-xs bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-lg hover:bg-emerald-100 transition-colors">Activate</button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 text-[12px] text-slate-500 border-t border-slate-100 pt-3">
            <div>Joined: {formatDate(detail.created_at)}</div>
            <div>Last login: {formatDate(detail.last_login_at)}</div>
            <div>Health state: {detail.health_state?.status || '—'} (v{detail.health_state?.version || 0})</div>
            <div>ID: <span className="font-mono text-[11px]">{detail.id}</span></div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Recent Chats</h4>
            {detail.recent_activity?.sessions?.length ? detail.recent_activity.sessions.map(s => (
              <div key={s.id} className="py-2 border-b border-slate-50 last:border-0">
                <p className="text-xs text-[#0F172A] truncate">{s.title}</p>
                <p className="text-[10px] text-slate-300">{s.message_count} msgs · {formatDate(s.last_activity)}</p>
              </div>
            )) : <EmptyState message="No chats" />}
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Recent Appointments</h4>
            {detail.recent_activity?.appointments?.length ? detail.recent_activity.appointments.map(a => (
              <div key={a.id} className="py-2 border-b border-slate-50 last:border-0">
                <p className="text-xs text-[#0F172A] truncate">{a.title}</p>
                <p className="text-[10px] text-slate-300">{a.date} · {a.status}</p>
              </div>
            )) : <EmptyState message="No appointments" />}
          </div>
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Recent Documents</h4>
            {detail.recent_activity?.documents?.length ? detail.recent_activity.documents.map(d => (
              <div key={d.id} className="py-2 border-b border-slate-50 last:border-0">
                <p className="text-xs text-[#0F172A] truncate">{d.filename}</p>
                <p className="text-[10px] text-slate-300">{d.extraction_status} · {formatDate(d.created_at)}</p>
              </div>
            )) : <EmptyState message="No documents" />}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-xs">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" />
          <input
            type="text" placeholder="Search users..." value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:border-slate-300"
            data-testid="user-search-input"
          />
        </div>
        <p className="text-xs text-slate-400">{total} users</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="border-b border-slate-100 text-[11px] text-slate-400 uppercase tracking-wider">
              <th className="text-left py-3 px-4 font-medium">User</th>
              <th className="text-left py-3 px-4 font-medium">Role</th>
              <th className="text-left py-3 px-4 font-medium">Status</th>
              <th className="text-left py-3 px-4 font-medium">Joined</th>
              <th className="text-right py-3 px-4 font-medium" />
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors cursor-pointer" onClick={() => loadDetail(u.id)} data-testid={`user-row-${u.id}`}>
                <td className="py-3 px-4">
                  <p className="text-sm text-[#0F172A] font-medium">{u.name || '—'}</p>
                  <p className="text-[11px] text-slate-400">{u.email}</p>
                </td>
                <td className="py-3 px-4"><Badge variant={u.role === 'admin' ? 'info' : 'default'}>{u.role}</Badge></td>
                <td className="py-3 px-4">
                  {u.active === false ? <Badge variant="error">Inactive</Badge> : u.flagged ? <Badge variant="warning">Flagged</Badge> : <Badge variant="success">Active</Badge>}
                </td>
                <td className="py-3 px-4 text-xs text-slate-400">{formatDate(u.created_at)}</td>
                <td className="py-3 px-4 text-right"><CaretRight size={14} className="text-slate-300 inline" /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && <div className="text-center py-8 text-sm text-slate-300">Loading...</div>}
        {!loading && users.length === 0 && <EmptyState message="No users found" />}
      </div>

      {total > 20 && (
        <div className="flex justify-center gap-2 mt-4">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="text-xs px-3 py-1.5 border border-slate-200 rounded-lg disabled:opacity-30">Previous</button>
          <span className="text-xs text-slate-400 py-1.5">Page {page}</span>
          <button disabled={page * 20 >= total} onClick={() => setPage(p => p + 1)} className="text-xs px-3 py-1.5 border border-slate-200 rounded-lg disabled:opacity-30">Next</button>
        </div>
      )}
    </div>
  );
};


/* ══════════════════════════════════════════════════
   MODULE 2: SYSTEM ACTIVITY
   ══════════════════════════════════════════════════ */
const ActivityModule = () => {
  const [logs, setLogs] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 100 });
      if (filter !== 'all') params.set('level', filter);
      const [logsRes, sumRes] = await Promise.all([
        axios.get(`${_API}/studio/activity?${params}`),
        axios.get(`${_API}/studio/activity/summary`),
      ]);
      setLogs(logsRes.data.logs);
      setSummary(sumRes.data);
    } catch { toast.error('Failed to load activity'); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const levelIcon = (level) => {
    if (level === 'error') return <XCircle size={14} className="text-red-400" weight="fill" />;
    if (level === 'warning') return <Warning size={14} className="text-amber-400" weight="fill" />;
    return <CheckCircle size={14} className="text-slate-300" weight="fill" />;
  };

  return (
    <div>
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <StatCard label="Total Users" value={summary.total_users} sub={`${summary.active_users} active`} />
          <StatCard label="Tasks Today" value={summary.tasks_today} sub={summary.tasks_failed_today ? `${summary.tasks_failed_today} failed` : 'All ok'} />
          <StatCard label="Errors Today" value={summary.errors_today} />
          <StatCard label="Warnings Today" value={summary.warnings_today} />
        </div>
      )}

      <div className="flex items-center gap-2 mb-4">
        {['all', 'info', 'warning', 'error'].map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${filter === f ? 'bg-[#0F172A] text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`} data-testid={`filter-${f}`}>
            {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
        <button onClick={fetchData} className="ml-auto text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1" data-testid="refresh-activity"><ArrowClockwise size={12} /> Refresh</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="activity-log">
        {loading ? (
          <div className="text-center py-8 text-sm text-slate-300">Loading...</div>
        ) : logs.length === 0 ? (
          <EmptyState message="No activity logged yet" />
        ) : (
          <div className="divide-y divide-slate-50">
            {logs.map(l => (
              <div key={l.id} className="flex items-start gap-3 px-4 py-3">
                <div className="mt-0.5">{levelIcon(l.level)}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-[#0F172A]">{l.message}</p>
                  <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-300">
                    <span>{l.category}</span>
                    {l.user_id && <span className="font-mono">{l.user_id.slice(0, 8)}</span>}
                    <span>{formatDate(l.timestamp)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


/* ══════════════════════════════════════════════════
   MODULE 3: BACKGROUND TASK MONITOR
   ══════════════════════════════════════════════════ */
const TasksModule = () => {
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 100 });
      if (filter) params.set('status', filter);
      const { data } = await axios.get(`${_API}/studio/tasks?${params}`);
      setTasks(data.tasks);
    } catch { toast.error('Failed to load tasks'); }
    setLoading(false);
  }, [filter]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const handleRetry = async (taskId) => {
    try {
      await axios.post(`${_API}/studio/tasks/${taskId}/retry`);
      toast.success('Task retry dispatched');
      setTimeout(fetchTasks, 2000);
    } catch (err) { toast.error(err.response?.data?.detail || 'Retry failed'); }
  };

  const statusBadge = (status) => {
    if (status === 'completed') return <Badge variant="success">Completed</Badge>;
    if (status === 'failed') return <Badge variant="error">Failed</Badge>;
    if (status === 'running') return <Badge variant="info">Running</Badge>;
    return <Badge>{status}</Badge>;
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        {['', 'running', 'completed', 'failed'].map(f => (
          <button key={f} onClick={() => setFilter(f)} className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${filter === f ? 'bg-[#0F172A] text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
            {f === '' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
        <button onClick={fetchTasks} className="ml-auto text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1" data-testid="refresh-tasks"><ArrowClockwise size={12} /> Refresh</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="tasks-list">
        {loading ? (
          <div className="text-center py-8 text-sm text-slate-300">Loading...</div>
        ) : tasks.length === 0 ? (
          <EmptyState message="No background tasks recorded yet" />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-[11px] text-slate-400 uppercase tracking-wider">
                <th className="text-left py-3 px-4 font-medium">Task</th>
                <th className="text-left py-3 px-4 font-medium">Status</th>
                <th className="text-left py-3 px-4 font-medium">Duration</th>
                <th className="text-left py-3 px-4 font-medium">Started</th>
                <th className="text-right py-3 px-4 font-medium" />
              </tr>
            </thead>
            <tbody>
              {tasks.map(t => (
                <tr key={t.id} className="border-b border-slate-50">
                  <td className="py-3 px-4">
                    <p className="text-xs text-[#0F172A] font-medium">{t.name}</p>
                    {t.user_id && <p className="text-[10px] text-slate-300 font-mono">{t.user_id.slice(0, 8)}</p>}
                  </td>
                  <td className="py-3 px-4">{statusBadge(t.status)}</td>
                  <td className="py-3 px-4 text-xs text-slate-400">{t.duration_ms ? `${(t.duration_ms / 1000).toFixed(1)}s` : '—'}</td>
                  <td className="py-3 px-4 text-xs text-slate-400">{formatDate(t.started_at)}</td>
                  <td className="py-3 px-4 text-right">
                    {t.status === 'failed' && (
                      <button onClick={() => handleRetry(t.id)} className="text-[11px] text-blue-500 hover:text-blue-700 flex items-center gap-1 ml-auto" data-testid={`retry-${t.id}`}>
                        <ArrowClockwise size={11} /> Retry
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {tasks.some(t => t.error) && (
        <div className="mt-4 bg-red-50 border border-red-100 rounded-lg p-4">
          <h4 className="text-xs font-semibold text-red-500 uppercase tracking-wider mb-2">Recent Errors</h4>
          {tasks.filter(t => t.error).slice(0, 5).map(t => (
            <div key={t.id} className="text-xs text-red-400 py-1 border-b border-red-50 last:border-0">
              <span className="font-medium text-red-500">{t.name}</span>: {t.error}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


/* ══════════════════════════════════════════════════
   MODULE 4: CONTENT MANAGEMENT
   ══════════════════════════════════════════════════ */
const ContentModule = () => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${_API}/studio/content`);
      setItems(data.items);
    } catch { toast.error('Failed to load content'); }
    setLoading(false);
  };

  useEffect(() => { fetchContent(); }, []);

  const startEdit = (item) => {
    setEditing(item.key);
    setEditValue(item.value || '');
  };

  const saveEdit = async () => {
    try {
      await axios.put(`${_API}/studio/content/${editing}`, { value: editValue });
      toast.success('Content updated');
      setEditing(null);
      fetchContent();
    } catch { toast.error('Failed to save'); }
  };

  const grouped = items.reduce((acc, item) => {
    const cat = item.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  return (
    <div>
      {loading ? (
        <div className="text-center py-8 text-sm text-slate-300">Loading...</div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([category, categoryItems]) => (
            <div key={category}>
              <h3 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-3">{category}</h3>
              <div className="space-y-2">
                {categoryItems.map(item => (
                  <div key={item.key} className="bg-white border border-slate-200 rounded-lg p-4" data-testid={`content-${item.key}`}>
                    {editing === item.key ? (
                      <div>
                        <p className="text-xs font-semibold text-[#0F172A] mb-2">{item.label}</p>
                        <textarea
                          value={editValue} onChange={e => setEditValue(e.target.value)}
                          className="w-full text-sm border border-slate-200 rounded-lg p-3 min-h-[100px] focus:outline-none focus:border-slate-300 font-mono text-xs"
                          data-testid={`content-edit-${item.key}`}
                        />
                        <div className="flex gap-2 mt-2">
                          <button onClick={saveEdit} className="text-xs bg-[#0F172A] text-white px-4 py-1.5 rounded-lg hover:bg-slate-800 transition-colors" data-testid="content-save-btn">Save</button>
                          <button onClick={() => setEditing(null)} className="text-xs text-slate-400 px-3 py-1.5 hover:text-slate-600 transition-colors">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-[#0F172A]">{item.label}</p>
                          <p className="text-[11px] text-slate-400 mt-1 truncate">{item.value || '(empty — click edit to set)'}</p>
                          {item.updated_at && <p className="text-[10px] text-slate-300 mt-1">Updated: {formatDate(item.updated_at)}</p>}
                        </div>
                        <button onClick={() => startEdit(item)} className="text-xs text-blue-500 hover:text-blue-700 px-2 py-1 flex-shrink-0" data-testid={`edit-${item.key}`}>Edit</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


/* ══════════════════════════════════════════════════
   MAIN STUDIO PAGE
   ══════════════════════════════════════════════════ */
const StudioPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeModule, setActiveModule] = useState('users');

  // Block non-admin
  if (!user || user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#FAFBFC] flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg font-semibold text-[#0F172A] mb-2">Access Denied</p>
          <p className="text-sm text-slate-400 mb-4">Kindra Studio is restricted to admin users.</p>
          <button onClick={() => navigate('/')} className="text-sm text-blue-500 hover:text-blue-700">Return home</button>
        </div>
      </div>
    );
  }

  const modules = [
    { id: 'users', label: 'Users', icon: Users },
    { id: 'activity', label: 'Activity', icon: Pulse },
    { id: 'tasks', label: 'Tasks', icon: ListChecks },
    { id: 'content', label: 'Content', icon: FileText },
  ];

  const renderModule = () => {
    switch (activeModule) {
      case 'users': return <UsersModule />;
      case 'activity': return <ActivityModule />;
      case 'tasks': return <TasksModule />;
      case 'content': return <ContentModule />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFBFC] flex" data-testid="studio-page">
      {/* Sidebar */}
      <aside className="w-[200px] bg-white border-r border-slate-200 flex flex-col flex-shrink-0">
        <div className="p-5 border-b border-slate-100">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 mb-1">
            <img src="/kindra-logo.png" alt="Kindra" className="h-5 object-contain" />
          </button>
          <p className="text-[10px] font-semibold text-slate-400 tracking-wider uppercase mt-1">Studio</p>
        </div>
        <nav className="flex-1 p-3">
          {modules.map(m => {
            const Icon = m.icon;
            const active = activeModule === m.id;
            return (
              <button
                key={m.id} onClick={() => setActiveModule(m.id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors mb-0.5 ${
                  active ? 'bg-[#0F172A] text-white' : 'text-slate-500 hover:bg-slate-50 hover:text-slate-700'
                }`}
                data-testid={`nav-${m.id}`}
              >
                <Icon size={16} weight={active ? 'fill' : 'regular'} />
                {m.label}
              </button>
            );
          })}
        </nav>
        <div className="p-4 border-t border-slate-100">
          <p className="text-[10px] text-slate-300">{user.name}</p>
          <p className="text-[10px] text-slate-300">{user.email}</p>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <h1 className="text-lg font-semibold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {modules.find(m => m.id === activeModule)?.label}
          </h1>
        </header>
        <div className="p-6">
          {renderModule()}
        </div>
      </main>
    </div>
  );
};

export default StudioPage;
